import { FC, SyntheticEvent, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import MainLayout from 'src/pages/private';
import { ProfileTab } from 'src/components/SettingsTab/ProfileTab';
import { CompanyTab } from 'src/components/SettingsTab/CompanyTab';
import { BillingTab } from 'src/components/SettingsTab/BillingTab';
import CustomTabs from 'src/components/StyledComponents/StyledTab';
import { fetchCurrentUserRole } from 'src/utils/user.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { apiUrls, billingBlockedStatuses } from 'src/utils/properties.utils';
import {
  OverflowContainer,
  JustifyStartContainer,
} from 'src/components/StyledComponents/StyledPageComponents';

const Settings: FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [isOwner, setIsOwner] = useState(false);
  const [billingRequired, setBillingRequired] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { showSnackbar } = StyledSnackbar();

  useEffect(() => {
    const checkUserRole = async () => {
      try {
        const isOwner = await fetchCurrentUserRole();
        setIsOwner(isOwner);
      } catch (e) {
        const errorMessage = await getApiErrorMessage(e);
        showSnackbar(errorMessage, 'error');
      }
    };

    checkUserRole();
  }, [navigate]);

  useEffect(() => {
    const state = location.state as {
      billingRequired?: boolean;
      selectedTab?: number;
      subscriptionStatus?: string;
    } | null;

    if (state?.billingRequired) {
      setBillingRequired(true);
      setSelectedTab(2);
    }

    if (state?.selectedTab !== undefined) {
      setSelectedTab(state.selectedTab);
    }

    if (searchParams.get('tab') === 'billing') {
      setSelectedTab(2);
    }

    const checkBillingStatus = async () => {
      try {
        const { data } = await axiosTokenInstance.get(
          apiUrls.SUBSCRIPTION_STATUS
        );
        const status = data?.data?.status;

        if (billingBlockedStatuses.includes(status)) {
          setBillingRequired(true);
          setSelectedTab(2);
        } else {
          setBillingRequired(false);
        }
      } catch {
        // Other settings data will show API-level errors if billing cannot load.
      }
    };

    checkBillingStatus();
  }, [location.state, searchParams]);

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  return (
    <MainLayout title="Settings">
      <Box display="flex">
        <OverflowContainer flexGrow={1} display="flex" flexDirection="column">
          <JustifyStartContainer display="flex">
            <CustomTabs
              selectedTab={selectedTab}
              handleTabChange={handleTabChange}
              isOwner={isOwner || billingRequired}
            />

            <Box flexGrow={1}>
              {selectedTab === 0 && <ProfileTab />}
              {selectedTab === 1 && <CompanyTab />}
              {selectedTab === 2 && (
                <BillingTab forcePaymentPrompt={billingRequired} />
              )}
            </Box>
          </JustifyStartContainer>
        </OverflowContainer>
      </Box>
    </MainLayout>
  );
};

export default Settings;
